'use client'

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef, SortingState, RowSelectionState } from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable
} from '@tanstack/react-table'
import { useDebounce } from '@uidotdev/usehooks'
import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { BanUserDialog } from '@/components/admin/users/ban-user-dialog'
import { ManageSessionsDialog } from '@/components/admin/users/manage-sessions-dialog'
import { SetRoleDialog } from '@/components/admin/users/set-role-dialog'
import { SendNotificationDialog } from '@/components/admin/users/send-notification-dialog'
import { ConfirmationDialog } from '@/components/confirmation-dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useConfirmationDialog } from '@/hooks/use-confirmation-dialog'
import { authClient } from '@/lib/auth-client'
import { trpc } from '@/lib/trpc'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowDown, ArrowUp, ArrowUpDown, PlusCircle, Search } from 'lucide-react'
import { AdminLayout } from '@/layouts/admin-layout'
import { type AdminUser, getUserColumns } from './columns'
import { useDebouncedCallback } from '@tanstack/react-pacer'

const DEFAULT_LIMIT = 10
const USER_LIST_QUERY_KEY = 'adminUserList'

interface ListUsersSuccessData {
	users: AdminUser[]
	total: number
}

interface BanUserPayload {
	userId: string
	banReason?: string
	banExpiresIn?: number
}
interface SetRolePayload {
	userId: string
	role: string
}
interface RevokeSessionVariables {
	sessionToken: string
}
interface RevokeAllSessionsPayload {
	userId: string
}
interface SendNotificationPayload {
	userId: string
	message: string
}

export default function AdminUsersPage() {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { data: session, isPending: isSessionLoading } = authClient.useSession()
	const { t } = useTranslation()

	const [pageIndex, setPageIndex] = useState(0)
	const [pageSize, setPageSize] = useState(DEFAULT_LIMIT)
	const [searchQuery, setSearchQuery] = useState('')
	const debouncedSearchQuery = useDebounce(searchQuery, 500)
	const [searchField, setSearchField] = useState<'email' | 'name' | 'id'>('email')
	const [userStatusFilter, setUserStatusFilter] = useState<string>('all') // 'all', 'active', 'banned'
	const [sorting, setSorting] = useState<SortingState>([])
	const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

	const [showBanConfirm, setShowBanConfirm] = useState(false)
	const [showSetRoleDialog, setShowSetRoleDialog] = useState(false)
	const [showSessionsDialog, setShowSessionsDialog] = useState(false)
	const [showSendNotificationDialog, setShowSendNotificationDialog] = useState(false)
	const [showCreateUserDialog, setShowCreateUserDialog] = useState(false) // For Create User Dialog
	const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)

	React.useEffect(() => {
		if (!isSessionLoading && session?.user?.role !== 'admin') {
			toast.error(t('toasts.accessDenied'))
			navigate('/')
		}
	}, [session, isSessionLoading, navigate, t])

	const fetchUsers = async (
		currentPageIndex: number,
		currentPageSize: number,
		search: string,
		currentSorting: SortingState,
		statusFilter: string
	): Promise<ListUsersSuccessData> => {
		const queryParams: {
			offset: number
			limit: number
			searchField?: string
			searchOperator?: string
			searchValue?: string
			sortBy?: string
			sortDirection?: 'asc' | 'desc'
			status?: 'active' | 'banned' // Add status to query params
		} = {
			offset: currentPageIndex * currentPageSize,
			limit: currentPageSize,
		}

		if (search && searchField) {
			queryParams.searchField = searchField
			queryParams.searchOperator = searchField === 'id' ? 'equals' : 'contains'
			queryParams.searchValue = search
		}

		if (statusFilter !== 'all') {
			queryParams.status = statusFilter as 'active' | 'banned'
		}

		if (currentSorting.length > 0) {
			const sort = currentSorting[0]
			queryParams.sortBy = sort.id
			queryParams.sortDirection = sort.desc ? 'desc' : 'asc'
		}

		// TODO: Update authClient.admin.listUsers to accept status filter
		const result = await authClient.admin.listUsers({ query: queryParams })

		if (result.error) {
			throw new Error(result.error.message || t('admin.users.page.loadingUsersError'))
		}
		if (!result.data || !Array.isArray(result.data.users) || typeof result.data.total !== 'number') {
			console.error('Unexpected response structure from listUsers API:', result)
			throw new Error(`${t('common.errorPrefix')}: Unexpected response structure from listUsers API`)
		}
		const users = result.data.users.map((user) => ({
			...user,
			roles: user.role ? user.role.split(',') : [],
			banned: !!user.banned,
		})) as AdminUser[]
		return { users, total: result.data.total }
	}

	const {
		data: usersData,
		isLoading: isLoadingUsers,
		isFetching: isFetchingUsers,
		error: usersError,
	} = useQuery({
		queryKey: [
			USER_LIST_QUERY_KEY,
			{
				pageIndex,
				pageSize,
				field: searchField,
				search: debouncedSearchQuery,
				sorting,
				status: userStatusFilter, // Add status filter to query key
			},
		],
		queryFn: () => fetchUsers(pageIndex, pageSize, debouncedSearchQuery, sorting, userStatusFilter),
		enabled: !isSessionLoading && session?.user?.role === 'admin',
		placeholderData: keepPreviousData,
		staleTime: 5 * 60 * 1000,
	})

	React.useEffect(() => {
		setPageIndex(0)
	}, [userStatusFilter]) // Reset page index when status filter changes

	const useAdminMutation = <TVariables, TData>(
		mutationFn: (vars: TVariables) => Promise<{ data?: TData; error?: { message?: string } }>,
		successMessage: string,
		errorMessagePrefix: string,
		onMutate?: (variables: TVariables) => void,
		onSettled?: () => void
	) => {
		return useMutation({
			mutationFn: async (variables: TVariables) => {
				const result = await mutationFn(variables)
				if (result.error) {
					throw new Error(result.error.message || `${t('common.errorPrefix')}: Mutation failed`)
				}
				return result.data ?? result
			},
			onMutate,
			onSuccess: () => {
				toast.success(successMessage)
				queryClient.invalidateQueries({
					queryKey: [
						USER_LIST_QUERY_KEY,
						{
							pageIndex,
							pageSize,
							field: searchField,
							search: debouncedSearchQuery,
							sorting,
							status: userStatusFilter, // Add status filter to query key for invalidation
						},
					],
				})
			},
			onError: (error) => {
				toast.error(`${t(errorMessagePrefix)}: ${error.message}`)
			},
			onSettled: onSettled,
		})
	}

	const banUserMutation = useAdminMutation<BanUserPayload, unknown>(
		authClient.admin.banUser,
		t('toasts.banSuccess'),
		t('toasts.banError'),
		undefined,
		() => {}
	)

	const unbanUserMutation = useAdminMutation<{ userId: string }, unknown>(
		authClient.admin.unbanUser,
		t('toasts.unbanSuccess'),
		t('toasts.unbanError'),
		undefined,
		() => {
			unbanDialog.setOpen(false)
		}
	)

	const deleteUserMutation = useAdminMutation<{ userId: string }, unknown>(
		authClient.admin.removeUser,
		t('toasts.deleteSuccess'),
		t('toasts.deleteError'),
		undefined,
		() => {
			deleteDialog.setOpen(false)
			if (usersData?.users.length === 1 && pageIndex > 0) {
				setPageIndex(Math.max(0, pageIndex - 1))
			}
		}
	)

	const setRoleMutation = useAdminMutation<SetRolePayload, unknown>(
		authClient.admin.setRole,
		t('toasts.setRoleSuccess'),
		t('toasts.setRoleError'),
		undefined,
		() => {
			setShowSetRoleDialog(false)
		}
	)

	const impersonateUserMutation = useAdminMutation<{ userId: string }, unknown>(
		authClient.admin.impersonateUser,
		t('toasts.impersonateSuccess'),
		t('toasts.impersonateError'),
		undefined,
		() => {
			impersonateDialog.setOpen(false)
			window.location.reload()
		}
	)

	const revokeSessionMutation = useAdminMutation<RevokeSessionVariables, unknown>(
		authClient.admin.revokeUserSession,
		t('toasts.revokeSessionSuccess'),
		t('toasts.revokeSessionError')
	)

	// TODO: Verify revokeAllUserSessions is available on authClient.admin and types are correct
	const revokeAllSessionsMutation = useAdminMutation<RevokeAllSessionsPayload, unknown>(
		authClient.admin.revokeUserSessions as any, // TEMP: Cast to any, assuming it might be revokeUserSessions or similar
		t('toasts.revokeAllSessionsSuccess'),
		t('toasts.revokeAllSessionsError'),
		undefined,
		() => {
			revokeAllSessionsDialog.setOpen(false)
		}
	)

	// TODO: Verify sendNotification is available on authClient.admin and types are correct
	const sendNotificationMutation = useAdminMutation<SendNotificationPayload, unknown>(
		authClient.admin.listUsers as any, // TEMP: Cast to any, placeholder, replace with actual if available
		t('toasts.sendNotificationSuccess'),
		t('toasts.sendNotificationError'),
		undefined,
		() => {
			setShowSendNotificationDialog(false)
		}
	)

	// Placeholder for Create User Mutation
	// const createUserMutation = useAdminMutation<CreateUserPayload, unknown>(
	//  authClient.admin.createUser, // Assuming this exists
	// 	t('toasts.createUserSuccess'),
	// 	t('toasts.createUserError'),
	// 	undefined,
	// 	() => {
	// 		setShowCreateUserDialog(false);
	// 	}
	// );

	const renderBulkActionsContent = (selectedRows: AdminUser[]) => {
		const handleBulkBanClick = () => {
			toast.info(t('admin.users.page.bulkBanNotImplemented'))
		}

		const handleBulkDeleteClick = () => {
			toast.info(t('admin.users.page.bulkDeleteNotImplemented'))
		}

		return (
			<div className='flex space-x-2'>
				<Button variant='destructive' size='sm' onClick={handleBulkBanClick}>
					{t('admin.users.actions.ban')} ({selectedRows.length})
				</Button>
				<Button variant='destructive' size='sm' onClick={handleBulkDeleteClick}>
					{t('admin.users.actions.delete')} ({selectedRows.length})
				</Button>
			</div>
		)
	}

	const onUnbanConfirm = useCallback(async () => {
		if (selectedUser) {
			await unbanUserMutation.mutateAsync({ userId: selectedUser.id });
		}
	}, [selectedUser, unbanUserMutation]);

	const unbanDialog = useConfirmationDialog({
		title: t('admin.users.actions.unban'),
		description: t('admin.users.confirmations.unban'),
		confirmText: t('admin.users.actions.unban'),
		cancelText: t('common.cancel'),
		onConfirm: onUnbanConfirm,
	})

	const onDeleteConfirm = useCallback(async () => {
		if (selectedUser) {
			await deleteUserMutation.mutateAsync({ userId: selectedUser.id });
		}
	}, [selectedUser, deleteUserMutation]);

	const deleteDialog = useConfirmationDialog({
		title: t('admin.users.actions.delete'),
		description: t('admin.users.confirmations.delete'),
		confirmText: t('admin.users.actions.delete'),
		cancelText: t('common.cancel'),
		onConfirm: onDeleteConfirm,
	})

	const onImpersonateConfirm = useCallback(async () => {
		if (selectedUser) {
			await impersonateUserMutation.mutateAsync({ userId: selectedUser.id });
		}
	}, [selectedUser, impersonateUserMutation]);

	const impersonateDialog = useConfirmationDialog({
		title: t('admin.users.actions.impersonate'),
		description: t('admin.users.confirmations.impersonate'),
		confirmText: t('admin.users.actions.impersonate'),
		cancelText: t('common.cancel'),
		onConfirm: onImpersonateConfirm,
	})

	const onRevokeAllSessionsConfirm = useCallback(async () => {
		if (selectedUser) {
			await revokeAllSessionsMutation.mutateAsync({ userId: selectedUser.id });
		}
	}, [selectedUser, revokeAllSessionsMutation]);

	const revokeAllSessionsDialog = useConfirmationDialog({
		title: t('admin.users.actions.revokeAllSessions'),
		description: t('admin.users.confirmations.revokeAllSessions'),
		confirmText: t('admin.users.actions.revokeAllSessions'),
		cancelText: t('common.cancel'),
		onConfirm: onRevokeAllSessionsConfirm,
	})

	const handleManageBan = useCallback((user: AdminUser) => {
		setSelectedUser(user)
		if (user.banned) {
			unbanDialog.openDialog()
		} else {
			setShowBanConfirm(true)
		}
	}, [unbanDialog])

	const handleDeleteUser = useCallback((user: AdminUser) => {
		setSelectedUser(user)
		deleteDialog.openDialog()
	}, [deleteDialog])

	const handleManageRole = useCallback((user: AdminUser) => {
		setSelectedUser(user)
		setShowSetRoleDialog(true)
	}, [])

	const handleImpersonateUser = useCallback((user: AdminUser) => {
		setSelectedUser(user)
		impersonateDialog.openDialog()
	}, [impersonateDialog])

	const handleManageSessions = useCallback((user: AdminUser) => {
		setSelectedUser(user)
		setShowSessionsDialog(true)
	}, [])

	const handleRevokeAllSessions = useCallback((user: AdminUser) => {
		setSelectedUser(user)
		revokeAllSessionsDialog.openDialog()
	}, [revokeAllSessionsDialog])

	const handleSendNotification = useCallback((user: AdminUser) => {
		setSelectedUser(user)
		setShowSendNotificationDialog(true)
	}, [])

	const handleRevokeSession = useCallback(async (sessionToken: string) => {
		await revokeSessionMutation.mutateAsync({ sessionToken })
	}, [revokeSessionMutation])

	const handleBanSubmit = useCallback(async (banReason?: string, banExpiresIn?: number) => {
		if (selectedUser) {
			await banUserMutation.mutateAsync({
				userId: selectedUser.id,
				banReason,
				banExpiresIn,
			})
			setShowBanConfirm(false)
		}
	}, [selectedUser, banUserMutation])

	const handleSetRole = useCallback(async (role: string) => {
		if (selectedUser) {
			await setRoleMutation.mutateAsync({
				userId: selectedUser.id,
				role,
			})
		}
	}, [selectedUser, setRoleMutation])

	const handleOnUnban = useCallback((user: AdminUser) => {
		setSelectedUser(user)
		unbanDialog.openDialog()
	}, [unbanDialog]) // Depends on unbanDialog because it calls unbanDialog.openDialog

	const handleSendNotificationSubmit = useCallback(async (message: string) => {
		if (selectedUser) {
			await sendNotificationMutation.mutateAsync({
				userId: selectedUser.id,
				message,
			})
		}
	}, [selectedUser, sendNotificationMutation])

	// Define columns after all handler functions are declared
	const columns = useMemo(
		() =>
			getUserColumns({
				t,
				onSetRole: handleManageRole,
				onBan: handleManageBan,
				onUnban: handleOnUnban, // Pass the memoized callback
				onDelete: handleDeleteUser,
				onImpersonate: handleImpersonateUser,
				onManageSessions: handleManageSessions,
				onSendNotification: handleSendNotification,
			}),
		[
			t,
			handleManageRole,
			handleManageBan,
			handleOnUnban, // Add the new memoized callback to the dependency array
			handleDeleteUser,
			handleImpersonateUser,
			handleManageSessions,
			handleSendNotification,
		]
	)

		// Create a tanstack table instance
	 const table = useReactTable({
	   data: usersData?.users ?? [],
	   columns,
	   getCoreRowModel: getCoreRowModel(),
	   getFilteredRowModel: getFilteredRowModel(),
	   enableRowSelection: true,
	   manualPagination: true,
	   manualSorting: true,
	   state: {
	     sorting,
	     rowSelection,
	     pagination: {
	       pageIndex,
	       pageSize,
	     },
	   },
	   onSortingChange: setSorting,
	   onRowSelectionChange: setRowSelection,
	   pageCount: usersData?.total ? Math.ceil(usersData.total / pageSize) : 0,
	 });

	 const selectedRowsData = table.getFilteredSelectedRowModel().rows.map((row) => row.original);

	return (
		<AdminLayout>
			<div className='container mx-auto py-10'>
				<div className='space-y-6'> {/* Increased spacing */}
					<div className="flex items-center justify-between mb-6">
						<div>
							<h1 className="text-3xl font-bold">{t('admin.users.page.title')}</h1>
							<p className="text-muted-foreground mt-1">
								{t('admin.users.page.description', 'Manage all users in the system, including their roles, status, and sessions.')}
							</p>
						</div>
						<Button onClick={() => setShowCreateUserDialog(true)}>
							<PlusCircle className="mr-2 h-4 w-4" /> {t('admin.users.page.createUser')}
						</Button>
					</div>

					{usersError && (
						<Alert variant='destructive' className='w-full'>
							<AlertTitle>{t('common.error')}</AlertTitle>
							<AlertDescription>
								{usersError instanceof Error ? usersError.message : t('admin.users.page.unknownError')}
							</AlertDescription>
						</Alert>
					)}

					<div className='flex flex-col md:flex-row gap-4 items-center'> {/* Aligned items center */}
						<div className='relative w-full sm:w-auto'> {/* Relative for icon positioning */}
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
							<Input
								type="search"
								placeholder={t('admin.users.page.searchByPlaceholder', {
									field: t(`admin.users.fields.${searchField}`).toLowerCase(),
								})}
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								className='w-full sm:w-[300px] pl-10' // Added padding for icon
							/>
						</div>
						<Select value={searchField} onValueChange={(value) => setSearchField(value as 'email' | 'name' | 'id')}>
							<SelectTrigger className='w-full sm:w-[150px]'>
								<SelectValue placeholder={t('admin.users.page.searchBy')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='email'>{t('admin.users.fields.email')}</SelectItem>
								<SelectItem value='name'>{t('admin.users.fields.name')}</SelectItem>
								<SelectItem value='id'>{t('admin.users.fields.id')}</SelectItem>
							</SelectContent>
						</Select>
						<Select value={userStatusFilter} onValueChange={setUserStatusFilter}>
							<SelectTrigger className="w-full sm:w-[180px]">
								<SelectValue placeholder={t('admin.users.filters.statusPlaceholder', 'Filter by status')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('admin.users.filters.allStatuses', 'All Statuses')}</SelectItem>
								<SelectItem value="active">{t('admin.users.filters.active', 'Active')}</SelectItem>
								<SelectItem value="banned">{t('admin.users.filters.banned', 'Banned')}</SelectItem>
							</SelectContent>
						</Select>
						{/* Placeholder for Role Filter - to be added if roles are well-defined */}
						{/* <Select> ... </Select> */}
					</div>

					<div className="space-y-4">
	           <div className="flex items-center justify-between gap-2">
	             <div className="flex-1">
	               {selectedRowsData.length > 0 && (
	                 <div className="flex items-center space-x-2">
	                   {renderBulkActionsContent(selectedRowsData)}
	                 </div>
	               )}
	             </div>
	           </div>

	           <div className="overflow-x-auto rounded-md border"> {/* Added border */}
	             <Table>
	               <TableHeader>
	                 {table.getHeaderGroups().map((headerGroup) => (
	                   <TableRow key={headerGroup.id}>
	                     {headerGroup.headers.map((header) => (
	                       <TableHead key={header.id} className="whitespace-nowrap"> {/* Prevent wrapping */}
	                         {header.isPlaceholder ? null : (
	                           <div
	                             className={
	                               header.column.getCanSort()
	                                 ? "flex cursor-pointer select-none items-center group" // Added group for hover
	                                 : ""
	                             }
	                             onClick={header.column.getToggleSortingHandler()}
	                             onKeyDown={(e) => {
	                               if (e.key === "Enter" || e.key === " ") {
	                                 e.preventDefault();
	                                 header.column.getToggleSortingHandler()?.(e);
	                               }
	                             }}
	                             role={header.column.getCanSort() ? "button" : undefined}
	                             tabIndex={header.column.getCanSort() ? 0 : undefined}
	                           >
	                             {flexRender(header.column.columnDef.header, header.getContext())}
	                             {{
	                               asc: <ArrowUp className="ml-2 h-4 w-4" />,
	                               desc: <ArrowDown className="ml-2 h-4 w-4" />,
	                             }[header.column.getIsSorted() as string] ?? (
	                               header.column.getCanSort() ? (
	                                 <ArrowUpDown className="ml-2 h-4 w-4 text-muted-foreground group-hover:text-foreground" /> // Style adjustments
	                               ) : null
	                             )}
	                           </div>
	                         )}
	                       </TableHead>
	                     ))}
	                   </TableRow>
	                 ))}
	               </TableHeader>
	               <TableBody>
	                 {isLoadingUsers || isFetchingUsers ? (
	                   // Skeleton loading state
	                   // biome-ignore lint/suspicious/noArrayIndexKey: Skeleton rows don't need stable IDs
	                   Array.from({ length: pageSize }).map((_, rowIndex) => (
	                     <TableRow key={`skeleton-row-${rowIndex}`}>
	                       {columns.map((column, colIndex) => (
	                         <TableCell
	                           key={`skeleton-cell-${rowIndex}-${column.id || `col-${colIndex}`}`}
	                           className="h-12 py-2" // Adjusted padding
	                         >
	                           <Skeleton className="h-5 w-full" /> {/* Using Skeleton component */}
	                         </TableCell>
	                       ))}
	                     </TableRow>
	                   ))
	                 ) : (
	                   table.getRowModel().rows?.length ? (
	                     table.getRowModel().rows.map((row) => (
	                       <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
	                         {row.getVisibleCells().map((cell) => (
	                           <TableCell key={cell.id} className="py-2"> {/* Adjusted padding */}
	                             {flexRender(cell.column.columnDef.cell, cell.getContext())}
	                           </TableCell>
	                         ))}
	                       </TableRow>
	                     ))
	                   ) : (
	                     <TableRow>
	                       <TableCell colSpan={columns.length} className="h-24 text-center">
	                         {t('admin.users.page.noResultsFound', 'No results found')}
	                       </TableCell>
	                     </TableRow>
	                   )
	                 )}
	               </TableBody>
	             </Table>
	           </div>

	           <div className="flex items-center justify-between px-2 py-4"> {/* Adjusted padding */}
	             <div className="flex-1 text-sm text-muted-foreground">
	               {t('dataTable.selectedRows', {
									count: table.getFilteredSelectedRowModel().rows.length,
									total: table.getFilteredRowModel().rows.length, // This should be total in current view
								})}
	             </div>
	             <div className="flex items-center space-x-6 lg:space-x-8">
	               <div className="flex items-center space-x-2">
	                 <span className="text-sm font-medium text-muted-foreground whitespace-nowrap"> {/* Adjusted font weight */}
	                   {t('dataTable.rowsPerPage')}
	                 </span>
	                 <Select
	                   value={`${pageSize}`}
	                   onValueChange={(value) => {
	                     const newPageSize = Number(value);
											setPageIndex(0); // Reset to first page on page size change
	                     setPageSize(newPageSize);
	                   }}
	                 >
	                   <SelectTrigger className="h-8 w-[70px]"> {/* Adjusted width */}
	                     <SelectValue placeholder={pageSize} />
	                   </SelectTrigger>
	                   <SelectContent side="top">
	                     {[10, 20, 30, 40, 50].map((size) => (
	                       <SelectItem key={size} value={`${size}`}>
	                         {size}
	                       </SelectItem>
	                     ))}
	                   </SelectContent>
	                 </Select>
	               </div>
	               <div className="flex w-[100px] items-center justify-center text-sm font-medium text-muted-foreground"> {/* Adjusted width and font weight */}
	                 {t('dataTable.pageInfo', {
									pageIndex: table.getState().pagination.pageIndex + 1,
									pageCount: table.getPageCount() > 0 ? table.getPageCount() : 1,
								})}
	               </div>
	               <div className="flex items-center space-x-2">
	                 <Button
	                   variant="outline"
	                   className="hidden h-8 w-8 p-0 lg:flex"
	                   onClick={() => table.setPageIndex(0)}
	                   disabled={!table.getCanPreviousPage()}
	                 >
	                   <span className="sr-only">{t('pagination.goToFirstPage')}</span>
	                   <ChevronsLeft className="h-4 w-4" />
	                 </Button>
	                 <Button
	                   variant="outline"
	                   className="h-8 w-8 p-0"
	                   onClick={() => table.previousPage()}
	                   disabled={!table.getCanPreviousPage()}
	                 >
	                   <span className="sr-only">{t('pagination.goToPreviousPage')}</span>
	                   <ChevronLeft className="h-4 w-4" />
	                 </Button>
	                 <Button
	                   variant="outline"
	                   className="h-8 w-8 p-0"
	                   onClick={() => table.nextPage()}
	                   disabled={!table.getCanNextPage()}
	                 >
	                   <span className="sr-only">{t('pagination.goToNextPage')}</span>
	                   <ChevronRight className="h-4 w-4" />
	                 </Button>
	                 <Button
	                   variant="outline"
	                   className="hidden h-8 w-8 p-0 lg:flex"
	                   onClick={() => table.setPageIndex(table.getPageCount() - 1)}
	                   disabled={!table.getCanNextPage()}
	                 >
	                   <span className="sr-only">{t('pagination.goToLastPage')}</span>
	                   <ChevronsRight className="h-4 w-4" />
	                 </Button>
	               </div>
	             </div>
	           </div>
	         </div>
				</div>

				<ConfirmationDialog {...unbanDialog.dialogProps} />
				<ConfirmationDialog {...deleteDialog.dialogProps} />
				<ConfirmationDialog {...impersonateDialog.dialogProps} />
				<ConfirmationDialog {...revokeAllSessionsDialog.dialogProps} />

				{/* Placeholder for CreateUserDialog */}
				{/* {showCreateUserDialog && selectedUser && (
					<CreateUserDialog
						isOpen={showCreateUserDialog}
						onOpenChange={setShowCreateUserDialog}
						// createUserMutation={createUserMutation} // Pass the mutation
					/>
				)} */}


				{selectedUser && (
					<>
						<BanUserDialog
							isOpen={showBanConfirm}
							onOpenChange={setShowBanConfirm}
							// Pass the mutation directly as per BanUserDialogProps
							banUserMutation={banUserMutation}
							user={selectedUser}
						/>
						<SetRoleDialog
							isOpen={showSetRoleDialog}
							onOpenChange={setShowSetRoleDialog}
							// Pass the mutation directly
							setRoleMutation={setRoleMutation}
							user={selectedUser}
						/>
						<ManageSessionsDialog
							isOpen={showSessionsDialog}
							onOpenChange={setShowSessionsDialog}
							user={selectedUser}
							authClient={authClient} // Pass authClient
							revokeSessionMutation={revokeSessionMutation}
							revokeAllSessionsMutation={revokeAllSessionsMutation}
						/>
						<SendNotificationDialog
							isOpen={showSendNotificationDialog}
							onOpenChange={setShowSendNotificationDialog}
							user={selectedUser}
							mutate={sendNotificationMutation.mutate} // Pass mutate function
							isPending={sendNotificationMutation.isPending} // Pass isPending status
						/>
					</>
				)}
			</div>
		</AdminLayout>
	)
}